import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiClient } from '../../api/apiContext';

/**
 * Opens a sale's printable receipt (FR-901) in a new tab.
 *
 * The receipt is a server-rendered HTML page behind SALE_READ, and the dashboard's session is a
 * bearer token, not a cookie: a plain link to it opens a tab that carries no token and is refused
 * (and a link relative to the dashboard opens a dashboard page that does not exist). So the page is
 * fetched like any other request, with the token, and the tab is pointed at what came back — the
 * same reason the report export fetches its CSV rather than linking to it.
 */
export function useOpenReceipt(clinicId: string) {
  const client = useApiClient();
  const { i18n } = useTranslation();
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const open = async (saleId: string, receiptNumber?: string) => {
    setError(null);
    setOpening(saleId);
    // Opened before the request, while the click still counts as the user's own: a tab opened
    // after an await is a pop-up as far as the browser is concerned, and is blocked.
    const tab = window.open('', '_blank');
    try {
      const page = await client.download(
        `/clinics/${clinicId}/sales/${saleId}/receipt?locale=${encodeURIComponent(i18n.language)}`,
      );
      const url = URL.createObjectURL(new Blob([page], { type: 'text/html;charset=utf-8' }));
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        // Pop-ups blocked after all: hand the page over as a file instead.
        const link = document.createElement('a');
        link.href = url;
        link.download = `${receiptNumber ?? 'receipt'}.html`;
        link.click();
      }
      // Long enough for the tab to load it; the receipt can always be opened again.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (caught) {
      tab?.close();
      setError(caught);
    } finally {
      setOpening(null);
    }
  };

  return { open, opening, error };
}
